/**
 * CategoryNavigationBlock Component
 * 
 * Displays a sticky category navigation bar that allows users to quickly
 * jump to different category sections in the menu.
 */

import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { CategoryNavigationBlockProps } from '@/types/menuBuilder';
import { useCachedCategories } from '@/hooks/useCache';
import { useBranch } from '@/contexts/BranchContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useTranslation } from 'react-i18next';
import { Branch } from '@/types/index';
import { useMenuBuilderStore } from '@/stores/menuBuilderStore';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';

interface CategoryNavigationBlockComponentProps {
  props: CategoryNavigationBlockProps;
  isPreview?: boolean;
  branch?: Branch;
  preloadedData?: PublicMenuData;
}

export const CategoryNavigationBlock = memo(function CategoryNavigationBlock({
  props,
  isPreview = false,
  branch: propBranch,
  preloadedData,
}: CategoryNavigationBlockComponentProps) {
  const { t } = useTranslation();
  const { selectedBranchId } = useBranch(true);
  const { branches } = useFilteredBranches();
  
  // Get global theme for font weight settings
  const builderTheme = useMenuBuilderStore(s => s.globalThemeOverrides);
  const globalTheme = preloadedData?.layout?.globalThemeOverrides || builderTheme;
  const isBold = globalTheme?.fontWeight === 'bold';

  const currentBranch = propBranch || branches.find(b => b.id === selectedBranchId);

  const {
    sticky,
    size,
    style,
    showIcons,
    showNames,
    iconSize,
    backgroundColor,
    activeColor,
  } = props;

  // Fetch categories
  const { data: categoriesData } = useCachedCategories(currentBranch?.id || '');
  const categories = categoriesData || [];

  if (!categories || categories.length === 0) {
    if (!isPreview) {
      return (
        <div className="p-4 text-center text-sm text-muted-foreground border-2 border-dashed rounded-lg">
          {t('menuBuilder.noCategories', 'No categories available')}
        </div>
      );
    }
    return null;
  }

  // Get size classes
  const getSizeClasses = () => {
    const iconSizeMap = {
      sm: showIcons ? 'w-8 h-8' : '',
      md: showIcons ? 'w-10 h-10' : '',
      lg: showIcons ? 'w-12 h-12' : '',
    };

    const textSizeMap = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    const paddingMap = {
      sm: 'px-2 py-1',
      md: 'px-3 py-2',
      lg: 'px-4 py-2',
    };

    return {
      icon: iconSizeMap[iconSize] || iconSizeMap.md,
      text: textSizeMap[size] || textSizeMap.md,
      padding: paddingMap[size] || paddingMap.md,
    };
  };

  // Get style classes
  const getStyleClasses = () => {
    const baseClasses = 'flex items-center gap-2 flex-shrink-0 cursor-pointer transition-all hover:scale-105';
    
    switch (style) {
      case 'pills':
        return `${baseClasses} rounded-full`;
      case 'chips':
        return `${baseClasses} rounded-lg`;
      case 'buttons':
        return `${baseClasses} rounded-md border`;
      default:
        return `${baseClasses} rounded-full`;
    }
  };

  const sizeClasses = getSizeClasses();
  const styleClasses = getStyleClasses();

  // Handle category navigation click
  const handleCategoryClick = (categoryId: string) => {
    const element = document.getElementById(categoryId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      className="py-3 px-2 rounded-lg bg-white shadow-sm overflow-hidden"
      style={{ backgroundColor: backgroundColor || '#ffffff' }}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <div
          className="flex gap-2 overflow-x-auto pb-1 no-scrollbar scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat: { id: string; name: string; image_url?: string }) => (
            <div
              key={cat.id}
              onClick={() => handleCategoryClick(cat.id)}
              className={cn(
                styleClasses,
                sizeClasses.padding,
                'bg-muted hover:bg-muted/80'
              )}
              style={activeColor ? { '--active-color': activeColor } as React.CSSProperties : undefined}
            >
              {showIcons && cat.image_url && (
                <div className={cn('rounded-full overflow-hidden flex-shrink-0', sizeClasses.icon)}>
                  <img
                    src={cat.image_url}
                    alt={cat.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {showNames && (
                <span className={cn(isBold ? 'font-bold' : 'font-medium', 'whitespace-nowrap', sizeClasses.text)}>
                  {cat.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
